import { gsap } from 'gsap';

const finish = (loading: HTMLElement | null) => {
  document.documentElement.classList.remove('index-loading');
  if (loading) gsap.set(loading, { display: 'none' });
};

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Radius (px) that reaches the farthest viewport corner from centre, + buffer. */
const coverRadius = () =>
  Math.hypot(window.innerWidth, window.innerHeight) / 2 + 2;

/** Curtain mask = body-bg field with a transparent circular hole of radius `r`. */
const holeMask = (r: number) =>
  `radial-gradient(circle ${r}px at 50% 50%, transparent 0px, transparent ${r}px, #000 ${r + 0.5}px)`;

/**
 * Initial page-load reveal curtain — a cinematic "viewfinder" iris.
 *
 * 1. A black disc with the brand mark pops up in the centre (scale), holds.
 * 2. The disc/curtain dissolves through an expanding circular hole (a
 *    radial-gradient mask on the curtain), revealing the page beneath it.
 *
 * `onReveal` is fired at the moment the iris starts opening — pass `initASwap`
 * here so the view's ASwap "show" animations play in sync with the reveal
 * (ASwap plays the current view's show animation on init). The timing knobs
 * below are the dials to fine-tune next.
 */
export const initLoading = (onReveal?: () => void) => {
  const loading = document.querySelector<HTMLElement>('.box-loading');
  const disc = document.querySelector<HTMLElement>('.box-loading__disc');
  const logo = document.querySelector<HTMLElement>('.box-loading__logo');

  // No curtain (CMS user / JS-off markup) or reduced motion: skip straight to
  // the page, still firing the reveal hook so ASwap inits.
  if (!loading || !disc || !logo || prefersReducedMotion()) {
    onReveal?.();
    finish(loading);
    return;
  }

  // --- Timing knobs (fine-tune next) ---
  const POP_DURATION = 0.25;    // disc scale-up (s)
  const HOLD = 0.65;            // pause on the logo before the recolor starts (s)
  const COLOR_DURATION = .5;  // logo black → body-bg recolor (s)
  const COLOR_LEAD = 0.1;      // how long the recolor starts before the iris (s)
  const IRIS_DURATION = 0.2;   // iris expansion (s)

  // Resolve the body-bg token to a concrete colour GSAP can interpolate to.
  const bodyBg = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-body-bg').trim();

  // Absolute timeline positions so the recolor and iris can overlap.
  const colorStart = POP_DURATION + HOLD;
  const irisStart = colorStart + COLOR_LEAD;

  const state = { r: 0 };
  const tl = gsap.timeline({ onComplete: () => finish(loading) });

  // Phase 1 — disc pops in and holds.
  tl.to(disc, { scale: 1, duration: POP_DURATION, ease: 'back.out(1.7)' }, 0);

  // Phase 2 — after the hold, the logo recolors black → body-bg (fading into
  // the curtain), starting COLOR_LEAD before the iris opens (the two overlap).
  tl.to(logo, { color: bodyBg, duration: COLOR_DURATION, ease: 'power1.out' }, colorStart);

  // Phase 3 — fire the page's show animations (ASwap init) as the iris opens,
  // then expand the transparent hole from centre to past the corners.
  tl.add(() => onReveal?.(), irisStart);
  tl.to(state, {
    r: coverRadius(),
    duration: IRIS_DURATION,
    ease: 'power2.inOut',
    onUpdate: () => {
      const mask = holeMask(state.r);
      loading.style.setProperty('mask-image', mask);
      loading.style.setProperty('-webkit-mask-image', mask);
    },
  }, irisStart);
};
